import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllScholarshipsQuery } from '../../../../../services/endpoints/orders/scholarship';
import { User } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { Scholarship } from '../../../../../types/orders.type';
import { scholarshipTypes } from '../../../../../utils/consts/orders/scholarships';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import {Forbidden} from "../../../../../components/error/Forbidden";

// RBAC-FE-ORDERS: View scholarships - matches backend isAdmin middleware
// Global Admin IS included for Orders domain (unlike Finance)
export const userCanView_Scholarships = (user: User | null) =>
    !!(user?.isSuper ||
       user?.isSupremeAdmin ||
       user?.isAdmin ||
       user?.isGlobal ||
       user?.isLocationAdmin);

// RBAC-FE-ORDERS: Create/Edit scholarships - matches backend isGlobal middleware
// Only Global, Super, and Supreme can modify
export const userCanCreateEdit_Scholarships = (user: User | null) =>
    !!(user?.isSuper ||
       user?.isSupremeAdmin ||
       user?.isGlobal);

// Column definition factory to pass currentUser
const createScholarshipsColumns = (currentUser: User | null): ColumnDef<Scholarship>[] => [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Scholarship Type',
    accessorKey: 'scholarship_type',
    cell: ({ getValue }) => scholarshipTypes[getValue<string>()]
  },
  {
    header: 'Percent Discount',
    accessorKey: 'percent_discount'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/orders/scholarships/${row.original.id}`}
          underline
        />
        {userCanCreateEdit_Scholarships(currentUser) && (
          <LinkCell
            content="Edit"
            url={`/admin/orders/scholarships/${row.original.id}/edit`}
            underline
          />
        )}
      </Stack>
    )
  }
];

export const ScholarshipsView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({ scope: '' });
  const canAccessScholarships = userCanView_Scholarships(currentUser);
  
  const { data, error, isLoading, isFetching } = useGetAllScholarshipsQuery(filters, {
    skip: !canAccessScholarships
  });
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const navigate = useNavigate();

  // Create columns with currentUser context
  const scholarshipsColumns = createScholarshipsColumns(currentUser);

  const filterFormFields: FilterField[] = [
    {
      name: 'scholarship_type',
      type: 'number',
      element: (
        <FormSelect
          name="scholarship_type"
          label="Scholarship Type"
          options={Object.entries(scholarshipTypes).map((type) => ({
            value: type[0],
            label: type[1]
          }))}
          selected_Value={Object.entries(scholarshipTypes)
            .map((type) => ({
              value: type[0],
              label: type[1]
            }))
            .find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'scholarship_type')
            )}
        />
      )
    }
  ];

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading scholarships', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setScholarships(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // RBAC-FE-ORDERS: Defense-in-depth guard
  if (!canAccessScholarships) {
    return <Forbidden />;
  }

  return (
    <div className="py-2">
      <SFCTable
        name="Scholarships"
        columns={scholarshipsColumns}
        data={scholarships}
        count={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters.data?.filterBy}
        indexDownloadPath="scholarships"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          userCanCreateEdit_Scholarships(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/orders/scholarships/new')}
            >
              New scholarship
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
